import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, ThemedCard, ThemedButton } from './ThemeContext';

/**
 * A unified component for suggesting a bot game after waiting for an opponent
 * Works in both the lobby and game page contexts
 * 
 * @param {Object} props Component props
 * @param {Object} props.game The waiting game object
 * @param {Function} props.onSwitchToBotGame Callback for switching to a bot game
 * @param {Function} props.onClose Callback for closing the suggestion
 * @param {boolean} props.inGamePage Whether this component is being used in the game page
 */
const WaitingGameSuggestion = ({
  game,
  onSwitchToBotGame,
  onClose,
  inGamePage = false
}) => {
  const { woodStyles } = useTheme();
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const lastDismissedTimeRef = useRef(null);

  // Waiting thresholds
  const initialWaitThreshold = 120000; // 2 minutes in milliseconds
  const reshowInterval = 60000; // 1 minute in milliseconds

  useEffect(() => {
    // Clear any existing timer when component mounts/unmounts
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    clearTimer();

    // Only proceed if we have a valid game in waiting status
    if (!game || game.status !== 'waiting') {
      setVisible(false);
      return clearTimer;
    }

    // Calculate how long the game has been waiting
    const createdAt = new Date(game.createdAt || Date.now());
    const waitingTime = Date.now() - createdAt.getTime();

    // If we haven't shown the suggestion yet and past initial threshold
    if (waitingTime >= initialWaitThreshold && !lastDismissedTimeRef.current) {
      // First time reaching threshold, show immediately
      setVisible(true);

      // Set timer for next appearance
      timerRef.current = setTimeout(() => {
        setVisible(true);
      }, reshowInterval);
    }
    // If the suggestion was previously dismissed
    else if (lastDismissedTimeRef.current) {
      const timeSinceDismissal = Date.now() - lastDismissedTimeRef.current;

      // If it's time to show again based on the reshowInterval
      if (timeSinceDismissal >= reshowInterval) {
        setVisible(true);

        // Reset the dismissed time to ensure proper intervals
        lastDismissedTimeRef.current = null;
      } else {
        // Schedule next appearance
        timerRef.current = setTimeout(() => {
          setVisible(true);
          lastDismissedTimeRef.current = null;
        }, reshowInterval - timeSinceDismissal);
      }
    }
    // We haven't reached the initial threshold yet
    else {
      // Calculate time until initial appearance
      const timeToInitialShow = initialWaitThreshold - waitingTime;

      if (timeToInitialShow > 0) {
        // Set timer for initial appearance
        timerRef.current = setTimeout(() => {
          setVisible(true);

          // Set timer for subsequent appearances
          timerRef.current = setTimeout(() => {
            setVisible(true);
          }, reshowInterval);
        }, timeToInitialShow);
      }
    }

    return clearTimer;
  }, [game]);

  const handleClose = () => {
    setVisible(false);
    lastDismissedTimeRef.current = Date.now();

    // If there's an existing timer, clear it and set a new one
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set timer to show again after reshowInterval
    timerRef.current = setTimeout(() => {
      setVisible(true);
      lastDismissedTimeRef.current = null;
    }, reshowInterval);

    if (onClose) onClose();
  };

  if (!visible) {
    return null;
  }

  return (
    <ThemedCard className="mb-4 border-yellow-500 border-2">
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className={`text-lg font-medium ${woodStyles.text.primary}`}>
              Still waiting for an opponent?
            </h3>
            <p className={`mt-1 ${woodStyles.text.secondary}`}>
              {inGamePage
                ? "It's been a while. Would you like to play against a bot instead?"
                : "No opponent has joined yet. Would you like to play against a bot instead?"}
            </p>
            <p className={`mt-1 text-xs ${woodStyles.text.secondary}`}>
              Note: Games without opponents will be automatically closed after 10 minutes.
            </p>
          </div>
          <button
            onClick={handleClose}
            className={`${woodStyles.text.secondary} hover:${woodStyles.text.primary} ml-2`}
          >
            ✕
          </button>
        </div>

        <div className="mt-4 flex justify-end space-x-3">
          <ThemedButton
            onClick={handleClose}
            variant="secondary"
          >
            Keep waiting
          </ThemedButton>
          <ThemedButton
            onClick={() => {
              setVisible(false);
              if (timerRef.current) clearTimeout(timerRef.current);
              onSwitchToBotGame(game);
            }}
            variant="primary"
          >
            Play vs Bot
          </ThemedButton>
        </div>
      </div>
    </ThemedCard>
  );
};

export default WaitingGameSuggestion;